import { FC, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { SearchableSelect } from '@/components/ui';

export interface LinkedInCompanyPage {
  organizationUrn: string;
  role: string;
  organizationName?: string;
  companyPageUrl?: string;
}

interface Props {
  onSelect?: (organizationUrn: string, companyPageUrl?: string) => void;
  selectedUrn?: string;
}

export const LinkedInCompanyPageSelector: FC<Props> = ({
  onSelect,
  selectedUrn,
}) => {
  const [companyPages, setCompanyPages] = useState<LinkedInCompanyPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>(selectedUrn || '');

  useEffect(() => {
    fetchCompanyPages();
  }, []);

  useEffect(() => {
    if (selectedUrn) {
      setSelected(selectedUrn);
    }
  }, [selectedUrn]);

  const fetchCompanyPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get<{ companyPages: LinkedInCompanyPage[] }>(
        '/users/linkedin-company-pages',
      );
      setCompanyPages(response.data.companyPages);

      // Auto-select first page if none selected and pages available
      if (response.data.companyPages.length > 0 && !selectedUrn && !selected) {
        const firstPage = response.data.companyPages[0];
        setSelected(firstPage.organizationUrn);
        // Use setTimeout to ensure state is updated before calling handleSelect
        setTimeout(() => {
          handleSelect(firstPage.organizationUrn, firstPage.companyPageUrl);
        }, 0);
      } else if (selectedUrn) {
        setSelected(selectedUrn);
        // If we have a selected URN, find the page and trigger onSelect with URL
        const selectedPage = response.data.companyPages.find(
          page => page.organizationUrn === selectedUrn
        );
        if (selectedPage?.companyPageUrl) {
          setTimeout(() => {
            onSelect?.(selectedUrn, selectedPage.companyPageUrl);
          }, 0);
        }
      }
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (organizationUrn: string, companyPageUrl?: string) => {
    setSelected(organizationUrn);

    // Find the selected page to get its URL if not provided
    const selectedPage = companyPages.find(page => page.organizationUrn === organizationUrn);
    const url = companyPageUrl || selectedPage?.companyPageUrl;

    // Just notify parent of selection, don't save to backend yet
    onSelect?.(organizationUrn, url);
  };

  if (loading) {
    return (
      <div className="text-sm text-gray-600">
        Loading company pages...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-red-600">
        Error: {error}
      </div>
    );
  }

  if (companyPages.length === 0) {
    return (
      <div className="text-sm text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-md p-3">
        No company pages found. Please ensure you have admin access to at least one LinkedIn company page.
      </div>
    );
  }

  if (companyPages.length === 1) {
    return (
      <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md p-3">
        <span className="font-medium">Posting to:</span>{' '}
        <span className="font-semibold">
          {companyPages[0].organizationName || 'Company Page'}
        </span>
        {companyPages[0].role && (
          <span className="text-gray-500 ml-2">({companyPages[0].role})</span>
        )}
      </div>
    );
  }

  // Convert company pages to SearchableSelect options
  const selectOptions = companyPages.map((page) => ({
    value: page.organizationUrn,
    label: page.organizationName || page.organizationUrn,
    subtitle: page.role,
  }));

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Select Company Page for Posting:
      </label>
      <SearchableSelect
        options={selectOptions}
        value={selected}
        onChange={(value) => {
          const selectedPage = companyPages.find(page => page.organizationUrn === value);
          if (selectedPage) {
            handleSelect(value, selectedPage.companyPageUrl);
          }
        }}
        disabled={loading}
        placeholder="Select a company page..."
        emptyMessage="No company pages available"
        loading={loading}
        className="w-full"
      />
    </div>
  );
};

